import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  SimpleChanges,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { WorkflowStateComponent } from './state/state.component';
import { WorkflowTransitionComponent } from './transition/transition.component';
import { CdkDragEnd } from '@angular/cdk/drag-drop';
import {
  FINAL_APPROVAL_STATES,
  IremboEndState,
  IremboTransition,
  Workflow,
  point,
  stateConfig,
  transitionConfig,
} from './models';
import LeaderLine from 'leader-line-new';
import { MatDialog } from '@angular/material/dialog';
import { Subject, Subscription } from 'rxjs';

@Component({
  selector: 'irembogov-state-machine',
  templateUrl: './state-machine.component.html',
  styleUrls: ['./state-machine.component.css'],
})
export class StateMachineComponent implements OnInit, OnChanges, OnDestroy {
  @ViewChildren(WorkflowStateComponent)
  stateComponents!: QueryList<WorkflowStateComponent>;
  @ViewChildren(WorkflowTransitionComponent)
  transitionComponents!: QueryList<WorkflowTransitionComponent>;
  @ViewChild('editor') editor!: ElementRef;
  @ViewChild('frame') frameElement!: ElementRef;
  @ViewChild('content') contentElement!: ElementRef;
  @ViewChild('linesWrapper') linesWrapperElement!: ElementRef;
  recalculateSubscription!: Subscription;

  @Input() isFullscreen = false;
  @Input() zoomAction$: Subject<'in' | 'out' | 'reset'> = new Subject();
  @Input() iremboWorkflow: IremboTransition[] = [];
  @Input() updateLine = 0;
  @Input() recalculateAction$: Subject<void> = new Subject();

  // Outputs
  @Output() updateIremboWorkflowEvent = new EventEmitter<any>();

  workflow: Workflow = {
    states: new Map(),
    transitions: [],
  };

  scale = 1;
  panX = 0;
  panY = 0;
  isPanning = false;
  lastPanPoint: { x: number; y: number } | null = null;
  _drawedLink: any[] = [];
  zoomSubscription!: Subscription;
  forceRecalculatePositions = false;

  constructor(private cd: ChangeDetectorRef) {}

  ngOnInit(): void {
    this.zoomSubscription = this.zoomAction$.subscribe(action => {
      this.handleZoomAction(action);
    });

    this.recalculateSubscription = this.recalculateAction$.subscribe(() => {
      this.recalculateAllPositions();
    });
    this.buildAndDrawWorkflow();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes['iremboWorkflow'] &&
      !changes['iremboWorkflow'].isFirstChange()
    ) {
      this.iremboWorkflow = JSON.parse(
        JSON.stringify(changes['iremboWorkflow'].currentValue)
      );
      this.cd.detectChanges();

      requestAnimationFrame(() => {
        this.buildAndDrawWorkflow();
        this.cd.detectChanges(); // Force change detection after drawing
      });
    } else if (changes['isFullscreen']) {
      this.onFullscreenChange();
    } else {
      this._drawedLink.forEach(element => {
        element.position();
      });
    }
  }

  ngOnDestroy() {
    if (this.zoomSubscription) {
      this.zoomSubscription.unsubscribe();
    }
    if (this.recalculateSubscription) {
      this.recalculateSubscription.unsubscribe();
    }
  }

  // Event listeners
  @HostListener('wheel', ['$event'])
  onWheel(event: WheelEvent) {
    event.preventDefault();
    const delta = event.deltaY > 0 ? -0.01 : 0.01;
    this.zoom(delta);
  }

  @HostListener('window:resize')
  onResize() {
    this.updateWrapperPosition();
    this.updateLines();
  }

  // Public methods
  getContentTransform(): string {
    return `translate(${this.panX}px, ${this.panY}px) scale(${this.scale})`;
  }

  startPan(event: MouseEvent) {
    if (this.isDraggableElement(event.target as HTMLElement)) {
      return;
    }
    this.isPanning = true;
    this.lastPanPoint = { x: event.clientX, y: event.clientY };
  }

  pan(event: MouseEvent) {
    if (!this.isPanning || !this.lastPanPoint) {
      return;
    }
    const dx = event.clientX - this.lastPanPoint.x;
    const dy = event.clientY - this.lastPanPoint.y;
    this.panX += dx;
    this.panY += dy;
    this.lastPanPoint = { x: event.clientX, y: event.clientY };
    this.updateLines();
    this.contentElement.nativeElement.style.transform =
      this.getContentTransform();
  }

  endPan() {
    if (this.isPanning) {
      this.isPanning = false;
      this.lastPanPoint = null;
    }
  }

  isDraggableElement(element: HTMLElement | null): boolean {
    while (element) {
      if (
        element.tagName.toLowerCase() === 'irembogov-workflow-state' ||
        element.tagName.toLowerCase() === 'irembogov-workflow-transition'
      ) {
        return true;
      }
      element = element.parentElement;
    }
    return false;
  }

  recalculateAllPositions() {
    this.forceRecalculatePositions = true;
    this.buildAndDrawWorkflow();
    this.forceRecalculatePositions = false;
  }

  zoom(delta: number) {
    const oldScale = this.scale;
    this.scale = Math.max(0.1, Math.min(this.scale + delta, 3));

    const frameRect = this.frameElement.nativeElement.getBoundingClientRect();
    const centerX = frameRect.width / 2;
    const centerY = frameRect.height / 2;

    this.panX -= (centerX / oldScale - centerX / this.scale) * this.scale;
    this.panY -= (centerY / oldScale - centerY / this.scale) * this.scale;

    this.contentElement.nativeElement.style.transform =
      this.getContentTransform();
    this.updateWrapperPosition();
    this.updateLines();
  }

  updateLines() {
    this._drawedLink.forEach(line => {
      line.position();
    });
  }

  updateWrapperPosition() {
    if (this.linesWrapperElement) {
      const wrapper = this.linesWrapperElement.nativeElement;
      wrapper.style.transform = 'none';
      const rectWrapper = wrapper.getBoundingClientRect();
      wrapper.style.transform = `translate(${-(
        rectWrapper.left + window.scrollX
      )}px, ${-(rectWrapper.top + window.scrollY)}px)`;
    }
  }

  buildAndDrawWorkflow() {
    this.workflow = {
      states: new Map(),
      transitions: [],
    };
    const linesWrapper = this.linesWrapperElement?.nativeElement;
    while (linesWrapper?.firstChild) {
      linesWrapper.removeChild(linesWrapper.firstChild);
    }
    this._drawedLink = [];
    this.buildWorkflow();
    setTimeout(() => this.drawWorkflowDiagram());
  }

  onDragMove() {
    this.updateLines();
  }

  onDragEndState(event: CdkDragEnd<string>) {
    const stateId: string = event.source.element.nativeElement.id;
    const fullStateName = stateId.split('#')[1]; // This should now be the full name including path

    this._updatePositionOfStateInIremboWorkflow(
      fullStateName,
      event.source.getFreeDragPosition()
    );
    this.updateWrapperPosition();
    this.updateLines();
  }

  onDragEndTransition(event: CdkDragEnd<string>) {
    const transitionId: string = event.source.element.nativeElement.id;
    const [_, startState, transitionName] = transitionId.split('#');

    this._updatePositionOfTransitionInIremboWorkflow(
      startState,
      transitionName,
      event.source.getFreeDragPosition()
    );
    this.updateWrapperPosition();
    this.updateLines();
  }

  isLoopbackAllowed(stateName: string): boolean {
    return !['APPROVED', 'REJECTED'].includes(stateName.toUpperCase());
  }
  getComplexStateName(
    stateName: string,
    event: string,
    startState: string
  ): string {
    if (
      stateName === 'NEW' ||
      (stateName === startState && this.isLoopbackAllowed(stateName))
    ) {
      return stateName;
    }
    return `${stateName}_FROM_${event}_FROM_${startState}`;
  }
  isComplexStateName(stateName: string): boolean {
    return stateName.includes('_FROM_');
  }
  getBaseStateName(stateName: string): string {
    return this.isComplexStateName(stateName)
      ? stateName.split('_FROM_')[0]
      : stateName;
  }
  buildWorkflow() {
    const columnWidth = 250;
    const rowHeight = 150;
    const currentX = 50;
    const currentY = 50;

    const processedStates = new Set<string>();
    const statePositions = new Map<string, { x: number; y: number }>();
    const columnMaxY = new Map<number, number>();

    // Helper to get base state name - similar to getBaseStateName for end states
    const getBaseStartState = (stateName: string): string => {
      if (this.isComplexStateName(stateName)) {
        return stateName.split('_FROM_')[0];
      }
      return stateName;
    };

    // Helper to find all transitions with the same base start state
    const findRelatedStarts = (baseState: string) => {
      return this.iremboWorkflow.filter(
        t => getBaseStartState(t.startState) === baseState
      );
    };

    const dfs = (startState: string, depth = 0) => {
      if (processedStates.has(startState)) return;
      processedStates.add(startState);

      // Get all transitions with this base start state
      const baseState = getBaseStartState(startState);
      const transitions = findRelatedStarts(baseState);

      transitions.forEach((transition, index) => {
        let maxYForColumn = columnMaxY.get(depth) || currentY;

        // Add start state
        if (!this.workflow.states.has(startState)) {
          let statePosition;

          if (!this.forceRecalculatePositions) {
            if (startState === 'NEW') {
              const newTransition = this.iremboWorkflow.find(
                t => t.startState === 'NEW'
              );
              if (newTransition?.position) {
                statePosition = {
                  x: newTransition.position.x - 150,
                  y: newTransition.position.y,
                };
              }
            } else {
              const existingTransition = this.iremboWorkflow.find(
                t =>
                  (t.endStateOne?.stateCode === startState &&
                    t.endStateOne?.position) ||
                  (t.endStateTwo?.stateCode === startState &&
                    t.endStateTwo?.position)
              );

              if (existingTransition) {
                statePosition =
                  existingTransition.endStateOne?.stateCode === startState
                    ? existingTransition.endStateOne.position
                    : existingTransition.endStateTwo?.position;
              }
            }
          }

          if (!statePosition) {
            statePosition = {
              x: currentX + depth * columnWidth,
              y: maxYForColumn,
            };
          }

          this.workflow.states.set(startState, {
            name: startState,
            position: statePosition,
          });
          statePositions.set(startState, statePosition);
          maxYForColumn += rowHeight;
        }

        const transitionPosition =
          !this.forceRecalculatePositions && transition.position
            ? transition.position
            : {
                x: currentX + depth * columnWidth + columnWidth / 2,
                y: maxYForColumn,
              };

        this.workflow.transitions.push({
          name: transition.event.toString(),
          startState: startState,
          endStateOne: transition.endStateOne,
          endStateTwo: transition.endStateTwo,
          authorisation: transition.authorisation,
          position: transitionPosition,
        });

        maxYForColumn += rowHeight;

        // Process end states
        const processEndState = (endState: any) => {
          if (endState) {
            const endStateName = endState.stateCode.toString();
            const complexStateName = this.getComplexStateName(
              endStateName,
              transition.event.toString(),
              startState
            );
            const nameToUse = this.isLoopbackAllowed(endStateName)
              ? endStateName
              : complexStateName;

            if (!this.workflow.states.has(nameToUse)) {
              let endStatePosition;

              if (!this.forceRecalculatePositions && endState.position) {
                endStatePosition = endState.position;
              } else {
                endStatePosition = {
                  x: currentX + (depth + 1) * columnWidth,
                  y: maxYForColumn,
                };
              }

              this.workflow.states.set(nameToUse, {
                name: endStateName,
                displayName: complexStateName,
                position: endStatePosition,
              });
              statePositions.set(nameToUse, endStatePosition);
              maxYForColumn += rowHeight;

              // Get all transitions starting from this end state's base name
              const nextTransitions = this.iremboWorkflow.filter(
                t => getBaseStartState(t.startState) === endStateName
              );

              if (nextTransitions.length > 0) {
                // If there are transitions, continue DFS with the base state name
                dfs(endStateName, depth + 2);
              }
            }
          }
        };

        processEndState(transition.endStateOne);
        processEndState(transition.endStateTwo);

        columnMaxY.set(depth, maxYForColumn);
      });
    };

    // Start DFS from the initial state "NEW"
    dfs('NEW');
  }

  getStateComponentbyName(name: string): WorkflowStateComponent | undefined {
    return this.stateComponents.find(
      element =>
        element.config.name === name || element.config.displayName === name
    );
  }

  drawWorkflowDiagram() {
    const defaultColor = 'grey';
    const defaultStartPlug = 'disc';

    this.transitionComponents.forEach(element => {
      // Draw link from start state
      this._drawedLink.push(
        new LeaderLine({
          start: this.getStateComponentbyName(element.config.startState)?.el
            .nativeElement,
          end: element.el.nativeElement,
          startSocket: 'auto',
          endSocket: 'auto',
          startPlug: defaultStartPlug,
          endPlug: 'arrow1',
          color: defaultColor,
          dash: { animation: true },
          size: 2,
        })
      );

      // Function to draw link to an end state
      const drawEndStateLink = (
        endState: IremboEndState | undefined,
        endStateElement: ElementRef
      ) => {
        if (endState) {
          const endStateName = endState.stateCode;
          const complexStateName = this.getComplexStateName(
            endStateName,
            element.config.name,
            element.config.startState
          );

          const nameToUse = this.isLoopbackAllowed(endStateName)
            ? endStateName
            : complexStateName;
          const endStateComponent = this.getStateComponentbyName(nameToUse);

          const isLoopback = this.isLoopback(
            element.config.startState,
            nameToUse
          );
          const color = isLoopback ? '#1a6be0' : 'grey';

          if (endStateComponent) {
            this._drawedLink.push(
              new LeaderLine({
                start: endStateElement.nativeElement,
                end: endStateComponent.el.nativeElement,
                startSocket: 'auto',
                endSocket: 'left',
                startPlug: defaultStartPlug,
                endPlug: 'arrow1',
                color: color,
                dash: { animation: true },
                size: 2,
              })
            );
          }
        }
      };

      // Draw link to EndState One
      drawEndStateLink(element.config.endStateOne, element.endStateOne);

      // Draw link to EndState Two
      drawEndStateLink(element.config.endStateTwo, element.endStateTwo);
    });
    const linesWrapper = this.linesWrapperElement.nativeElement;

    const leaderLines = document.querySelectorAll('.leader-line');
    leaderLines.forEach(line => {
      linesWrapper.appendChild(line);
    });

    this.updateWrapperPosition();
  }
  //  method to detect loopback transitions
  isLoopback(startState: string, endState: string): boolean {
    const startIndex = this.getStateIndex(startState);
    const endIndex = this.getStateIndex(endState);
    return endIndex !== -1 && startIndex !== -1 && endIndex <= startIndex;
  }

  //  helper method to get the index of a state in the workflow
  getStateIndex(stateName: string): number {
    const stateEntries = Array.from(this.workflow.states.entries());
    return stateEntries.findIndex(
      ([key, value]) =>
        value.name === stateName || value.displayName === stateName
    );
  }

  updatePositionInIremboWorkflow() {
    this.transitionComponents.forEach(element => {
      this._updatePositionOfTransitionInIremboWorkflow(
        element.config.startState,
        element.config.name,
        element.config.position
      );
    });

    this.stateComponents.forEach(element => {
      this._updatePositionOfStateInIremboWorkflow(
        element.config.name,
        element.config.position
      );
    });
  }

  _updatePositionOfTransitionInIremboWorkflow(
    statename: string,
    eventname: string,
    position: point
  ) {
    this.iremboWorkflow.forEach(element => {
      if (element.event == eventname && element.startState == statename) {
        element.position = position;
        return;
      }
    });
    this.updateWorkflow();
  }

  _updatePositionOfStateInIremboWorkflow(
    stateNameWithPath: string,
    position: point
  ) {
    // Helper to check if this is the correct end state by matching paths
    const isMatchingState = (endState: any, transition: any) => {
      // If it's a simple state (like NEW), just match the name
      if (!this.isComplexStateName(stateNameWithPath)) {
        return endState.stateCode === stateNameWithPath;
      }

      // For complex names, reconstruct the path and compare
      const complexName = this.getComplexStateName(
        endState.stateCode,
        transition.event.toString(),
        transition.startState
      );
      return complexName === stateNameWithPath;
    };

    this.iremboWorkflow.forEach(transition => {
      // Check and update endStateOne
      if (
        transition.endStateOne &&
        isMatchingState(transition.endStateOne, transition)
      ) {
        transition.endStateOne.position = position;
      }

      // Check and update endStateTwo
      if (
        transition.endStateTwo &&
        isMatchingState(transition.endStateTwo, transition)
      ) {
        transition.endStateTwo.position = position;
      }
    });

    this.updateWorkflow();
  }

  onUpdateTransition(updatedTransition: any) {
    const newTransition = updatedTransition.result;
    const oldTransition = updatedTransition.config;

    if (newTransition === 'delete') {
      // Remove the oldTransition from iremboWorkflow
      this.iremboWorkflow = this.iremboWorkflow.filter(
        element =>
          !(
            element.event === oldTransition.name &&
            element.startState === oldTransition.startState
          )
      );
    } else {
      // Update existing transition
      this.iremboWorkflow.forEach(element => {
        if (
          element.event === oldTransition.name &&
          element.startState === oldTransition.startState
        ) {
          Object.assign(element, newTransition);
          return;
        }
      });
    }

    this.updateWorkflow();
  }

  updateWorkflow() {
    this.iremboWorkflow = JSON.parse(JSON.stringify(this.iremboWorkflow));
    this.updateIremboWorkflowEvent.emit(this.iremboWorkflow);
  }

  handleZoomAction(action: 'in' | 'out' | 'reset') {
    const zoomDelta = 0.01;

    switch (action) {
      case 'in':
        this.zoom(zoomDelta);
        break;
      case 'out':
        this.zoom(-zoomDelta);
        break;
      case 'reset':
        this.resetZoom();
        break;
    }
  }

  resetZoom() {
    this.scale = 1;
    this.panX = 0;
    this.panY = 0;
    this.contentElement.nativeElement.style.transform =
      this.getContentTransform();
    this.updateWrapperPosition();
    this.updateLines();
  }

  onFullscreenChange() {
    this.updateWrapperPosition();
    this.updateLines();
  }
}
